import { isEqual } from 'lodash';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, ModalUI, RadioButtonsGroup, useQueryFetch } from 'sonata-react-components';

import { CsvDropzone } from '@/components/common/CsvDropzone/CsvDropzone';
import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { deviceTargetingImportFile } from '@/resources/deviceTargeting.resources';
import { getDeviceTargetingTags } from './helpers/getSectionHeaderTags.helper';

import './deviceTargeting.scss';

type State = {
  file?: File;
};

type Props = {
  campaignId: string | undefined;
  sessionId: string;
};

function DeviceTargeting({ campaignId, sessionId }: Props) {
  const templateCsv = `${taptap.config.sonataStaticsUrlBase}/samples/deviceTargeting/exampleDeviceId.csv`;
  const { formatMessage } = useIntl();
  const importModalRef: any = useRef();

  const { setDeviceTargetingProps, targeting, user, resetSection } = useTargetingContext();

  const { targetUsers } = targeting;
  const [{ file }, setState] = useState<State>({
    file: undefined
  });

  const setDeviceTargeting = (options: any, enabled = true) => {
    setDeviceTargetingProps('options', options);
    setDeviceTargetingProps('enabled', enabled);
  };

  const { isLoading } = useQueryFetch(
    ['deviceTargetingImportFile', file, campaignId],
    async () => {
      const res = await deviceTargetingImportFile(user.id!, campaignId!, file!, sessionId);
      if (res && !isEqual(res.filePath, targetUsers.options.filePath)) {
        setDeviceTargeting({
          ...targetUsers.options,
          fileName: res.fileName,
          filePath: res.filePath,
          processId: Math.floor(Math.random() * 0x1000000000).toString(16),
          processState: 'processing',
          totalLines: res.totalLines
        });
      }
    },
    {
      enabled: !!file && !!campaignId,
      staleTime: 0
    }
  );

  const radioButtonGroupItems = [
    {
      id: 'include',
      text: formatMessage({
        defaultMessage: 'Include',
        id: 'targeting.deviceTargeting.radios.include'
      })
    },
    {
      id: 'exclude',
      text: formatMessage({
        defaultMessage: 'Exclude',
        id: 'targeting.deviceTargeting.radios.exclude'
      })
    }
  ];

  const handleFileSelected = (newFile: File) => {
    setState(prev => ({ ...prev, file: newFile }));
    setDeviceTargeting({ ...targetUsers.options, fileName: newFile.name });
    importModalRef?.current?.closeModal();
  };

  const handleRadioButtonListChange = (val: string) => {
    setDeviceTargeting({
      ...targetUsers.options,
      type: val
    });
  };

  const handleResetClick = () => {
    setState(prev => ({
      ...prev,
      file: undefined
    }));
    setDeviceTargeting({
      ...targetUsers.options,
      fileName: undefined,
      filePath: undefined,
      processId: undefined,
      processState: 'notProcessed',
      totalLines: 0
    });
  };

  const handleResetDeviceTargeting = () => {
    handleResetClick();
    resetSection('deviceTargeting');
  };

  const isSectionOpened = targetUsers.enabled;
  const deviceTargetingTags = getDeviceTargetingTags(targetUsers);

  return (
    <div className="deviceTargeting" id="deviceTargetingBox">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        onReset={handleResetDeviceTargeting}
        preffixId="deviceTargeting"
        tags={deviceTargetingTags}
        title={formatMessage({
          defaultMessage: 'Device Targeting',
          id: 'targeting.deviceTargeting.title'
        })}
        tooltipText={formatMessage({
          defaultMessage: 'Upload a list of specific Device IDs to include or exclude these users from your campaign',
          id: 'targeting.deviceTargeting.tooltip'
        })}
      >
        <div className="deviceTargeting__content">
          <RadioButtonsGroup
            id={'deviceTargetingRadioButtonList'}
            items={radioButtonGroupItems}
            onChange={handleRadioButtonListChange}
            selectedItem={targetUsers.options.type}
          />
          {!targetUsers.options.fileName ? (
            <ButtonUI
              className="deviceTargeting__contentUpload"
              icon="fa-solid fa-up-from-dotted-line"
              id={'deviceTargetingUpload'}
              onClick={() => importModalRef?.current?.openModal()}
              shape="squareRounded"
              text={formatMessage({
                defaultMessage: 'Upload Device File',
                id: 'targeting.deviceTargeting.upload'
              })}
              type="secondary"
            />
          ) : (
            <div className="deviceTargeting__contentLoading">
              {isLoading ? (
                <i className="fa-solid fa-spinner fa-spin deviceTargeting__contentLoadingSpinner" />
              ) : (
                <i className="fa-regular fa-circle-check deviceTargeting__contentLoadingIcon" />
              )}
              <span className="deviceTargeting__contentLoadingFile">{targetUsers.options.fileName}</span>
              <ButtonUI
                className="deviceTargeting__contentLoadingDelete"
                icon="fa-regular fa-trash"
                id={'deviceTargetingLoadingReset'}
                onClick={() => handleResetClick()}
                shape="link"
                type="secondary"
              />
            </div>
          )}
        </div>
      </FormExpandableSection>
      <ModalUI
        ref={importModalRef}
        title={formatMessage({
          defaultMessage: 'Import your Device ID',
          id: 'targeting.deviceTargeting.modal.title'
        })}
        width="805px"
      >
        <CsvDropzone
          inputId="deviceTargetingImportCsv"
          onFile={handleFileSelected}
          templateLabel={formatMessage({
            defaultMessage: 'Download Template',
            id: 'targeting.deviceTargeting.modal.download'
          })}
          templateUrl={templateCsv}
          uploadLabel={formatMessage({
            defaultMessage: 'Upload File',
            id: 'targeting.deviceTargeting.modal.upload'
          })}
        />
      </ModalUI>
    </div>
  );
}

export default DeviceTargeting;
